<template>
    <main role="main" class="sc-v2">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/statistiques" class="tbv2-crumb">
                    {{ $t('statistics.dashboard.title') }}
                </router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ $t('statistics.config.title') }}</span>
            </div>
        </portal>

        <header class="sc-hero">
            <div class="sc-hero-left">
                <div class="sc-hero-icon">
                    <i class="fal fa-sliders-h"></i>
                </div>
                <div class="sc-hero-body">
                    <h1 class="sc-hero-title">{{ $t('statistics.config.title') }}</h1>
                    <p class="sc-hero-sub">{{ $t('statistics.config.subtitle') }}</p>
                </div>
            </div>
            <div class="sc-hero-actions">
                <BmvButton variant="ghost" size="sm" @click="cancel">
                    {{ $t('statistics.config.cancel') }}
                </BmvButton>
                <BmvButton
                    variant="primary"
                    size="sm"
                    icon="fal fa-save"
                    :loading="saving"
                    :disabled="!dirty"
                    @click="save"
                >
                    {{ $t('statistics.config.save') }}
                </BmvButton>
            </div>
        </header>

        <div v-if="loading" class="sc-loading">
            <i class="fal fa-spinner-third fa-spin"></i>
        </div>

        <template v-else>
            <div class="sc-stats">
                <div class="sc-stats-progress">
                    <div class="sc-stats-progress-track">
                        <div
                            class="sc-stats-progress-bar"
                            :style="{ width: progressPercent + '%' }"
                        ></div>
                    </div>
                </div>
                <div class="sc-stats-text">
                    <strong>{{ selected.length }}</strong>
                    <span class="sc-stats-text-sep">/</span>
                    <span>{{ catalog.length }}</span>
                    <span class="sc-stats-text-label">
                        {{ $t('statistics.config.metrics_selected') }}
                    </span>
                </div>
                <div class="sc-stats-actions">
                    <button
                        type="button"
                        class="sc-stats-action"
                        :disabled="selected.length === catalog.length"
                        @click="selectAll"
                    >
                        <i class="fal fa-check-double"></i>
                        {{ $t('statistics.config.select_all') }}
                    </button>
                    <button
                        type="button"
                        class="sc-stats-action"
                        :disabled="!selected.length"
                        @click="clearAll"
                    >
                        <i class="fal fa-times"></i>
                        {{ $t('statistics.config.clear_all') }}
                    </button>
                </div>
            </div>

            <div v-if="!selected.length && catalog.length" class="sc-warning">
                <i class="fal fa-exclamation-circle"></i>
                <span>{{ $t('statistics.config.empty_selection_warning') }}</span>
            </div>

            <div v-if="catalog.length" class="sc-grid">
                <button
                    v-for="metric in catalog"
                    :key="metric.id"
                    type="button"
                    class="metric-card"
                    :class="{ 'metric-card--selected': isSelected(metric.id) }"
                    @click="toggle(metric.id)"
                >
                    <span class="metric-card-check" aria-hidden="true">
                        <i class="fal fa-check"></i>
                    </span>
                    <div class="metric-card-illustration" :class="`metric-card-illustration--${metric.display}`">
                        <i :class="displayIcon(metric.display)"></i>
                    </div>
                    <div class="metric-card-body">
                        <h3 class="metric-card-title">{{ metric.label }}</h3>
                        <p v-if="metric.description" class="metric-card-desc">
                            {{ metric.description }}
                        </p>
                    </div>
                    <div class="metric-card-meta">
                        <span class="metric-card-tag metric-card-tag--display">
                            <i :class="displayIcon(metric.display)"></i>
                            {{ $t(`statistics.config.display.${metric.display}`) }}
                        </span>
                        <span class="metric-card-tag">
                            {{ $t(`statistics.config.unit.${metric.unit}`) }}
                        </span>
                    </div>
                </button>
            </div>

            <div v-else class="sc-empty">
                <i class="fal fa-puzzle-piece"></i>
                <h3>{{ $t('statistics.config.empty_title') }}</h3>
                <p>{{ $t('statistics.config.empty_desc') }}</p>
            </div>
        </template>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'

const DISPLAY_ICONS = {
    card: 'fal fa-tachometer-alt',
    gauge: 'fal fa-tachometer-alt-fast',
    pie: 'fal fa-chart-pie',
    bar: 'fal fa-chart-bar',
    line: 'fal fa-chart-line',
    area: 'fal fa-chart-area'
}

export default {
    name: 'StatisticsConfig',
    components: { BmvButton },
    data() {
        return {
            loading: true,
            selected: []
        }
    },
    computed: {
        ...mapState('statistics', {
            catalog: (s) => s.catalog || [],
            config: (s) => s.config,
            saving: (s) => s.saving
        }),
        catalogIds() {
            return this.catalog.map((m) => m.id)
        },
        savedIds() {
            return (this.config && this.config.ids) || []
        },
        dirty() {
            if (this.selected.length !== this.savedIds.length) return true
            const a = [...this.selected].sort()
            const b = [...this.savedIds].sort()
            return a.some((id, i) => id !== b[i])
        },
        progressPercent() {
            if (!this.catalog.length) return 0
            return Math.round((this.selected.length / this.catalog.length) * 100)
        }
    },
    async created() {
        this.loading = true
        try {
            await Promise.all([
                this.$store.dispatch('statistics/fetchCatalog'),
                this.$store.dispatch('statistics/fetchConfig')
            ])
            this.selected = [...this.savedIds].filter((id) => this.catalogIds.includes(id))
        } catch (e) {
            this.$store.dispatch('dispatchError', e)
        } finally {
            this.loading = false
        }
    },
    methods: {
        isSelected(id) {
            return this.selected.includes(id)
        },
        toggle(id) {
            if (this.isSelected(id)) {
                this.selected = this.selected.filter((s) => s !== id)
            } else {
                this.selected = [...this.selected, id]
            }
        },
        selectAll() {
            this.selected = [...this.catalogIds]
        },
        clearAll() {
            this.selected = []
        },
        displayIcon(display) {
            return DISPLAY_ICONS[display] || 'fal fa-cube'
        },
        cancel() {
            this.$router.push('/statistiques').catch(() => {})
        },
        async save() {
            try {
                await this.$store.dispatch('statistics/fetchCatalog', { force: true })
                const validIds = this.selected.filter((id) => this.catalogIds.includes(id))
                await this.$store.dispatch('statistics/updateConfig', validIds)
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t('statistics.config.saved')
                })
                this.$router.push('/statistiques').catch(() => {})
            } catch (e) {
                this.$store.dispatch('dispatchError', e)
            }
        }
    }
}
</script>

<style scoped>
.sc-v2 {
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover { color: var(--v2-primary); }
.tbv2-sep { color: var(--v2-text-4); }
.tbv2-current { color: var(--v2-text-1); }

/* Hero */
.sc-hero {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-5);
    flex-wrap: wrap;
}

.sc-hero-left {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    flex: 1;
    min-width: 240px;
}

.sc-hero-icon {
    width: 52px;
    height: 52px;
    border-radius: var(--v2-radius-lg);
    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
    color: #fff;
    font-size: 1.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.25);
}

.sc-hero-icon i { color: inherit !important; }

.sc-hero-body {
    flex: 1;
    min-width: 0;
}

.sc-hero-title {
    margin: 0 0 4px;
    font-size: 1.5rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.sc-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.45;
}

.sc-hero-actions {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.sc-loading {
    display: flex;
    justify-content: center;
    padding: var(--v2-space-6);
    color: var(--v2-text-3);
    font-size: 1.5rem;
}

/* Stats / progress bar */
.sc-stats {
    display: flex;
    align-items: center;
    gap: var(--v2-space-4);
    padding: 14px 18px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    margin-bottom: var(--v2-space-3);
    flex-wrap: wrap;
}

.sc-stats-progress {
    flex: 1;
    min-width: 180px;
}

.sc-stats-progress-track {
    height: 6px;
    background: var(--v2-surface-2);
    border-radius: 999px;
    overflow: hidden;
}

.sc-stats-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #0891b2 0%, #06b6d4 100%);
    border-radius: 999px;
    transition: width 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.sc-stats-text {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    font-size: 0.92rem;
    color: var(--v2-text-2);
    font-variant-numeric: tabular-nums;
}

.sc-stats-text strong {
    font-size: 1.1rem;
    color: var(--v2-primary);
    font-weight: var(--v2-font-weight-bold);
}

.sc-stats-text-sep {
    color: var(--v2-text-4);
    margin: 0 2px;
}

.sc-stats-text-label {
    color: var(--v2-text-3);
    font-size: 0.78rem;
    margin-left: 6px;
}

.sc-stats-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.sc-stats-action {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: transparent;
    border: 1px solid transparent;
    color: var(--v2-text-3);
    font: inherit;
    font-size: 0.78rem;
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    border-radius: var(--v2-radius-sm);
    transition: background var(--v2-transition-fast), color var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.sc-stats-action i {
    font-size: 0.72rem;
    color: inherit !important;
}

.sc-stats-action:hover:not(:disabled) {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    border-color: var(--v2-primary-soft);
}

.sc-stats-action:disabled {
    color: var(--v2-text-4);
    cursor: not-allowed;
}

/* Warning banner */
.sc-warning {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(245, 158, 11, 0.08);
    border: 1px solid rgba(245, 158, 11, 0.25);
    border-radius: var(--v2-radius-md);
    margin-bottom: var(--v2-space-3);
    color: #b45309;
    font-size: 0.85rem;
}

.sc-warning i {
    color: #b45309;
    font-size: 1rem;
}

/* Grid */
.sc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--v2-space-3);
}

/* Card */
.metric-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
    background: var(--v2-surface-0);
    border: 2px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: inherit;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast), transform var(--v2-transition-fast), background var(--v2-transition-fast);
}

.metric-card:hover {
    border-color: var(--v2-border-strong);
    box-shadow: var(--v2-shadow-sm);
    transform: translateY(-1px);
}

.metric-card--selected {
    border-color: var(--v2-primary);
    background: var(--v2-primary-soft);
    box-shadow: 0 0 0 4px rgba(8, 145, 178, 0.08);
}

.metric-card--selected:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 4px rgba(8, 145, 178, 0.12), var(--v2-shadow-sm);
}

.metric-card-check {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--v2-primary);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    transform: scale(0);
    opacity: 0;
    transition: transform 0.18s cubic-bezier(0.22, 1.4, 0.36, 1), opacity 0.18s ease;
    box-shadow: 0 2px 6px rgba(8, 145, 178, 0.3);
}

.metric-card--selected .metric-card-check {
    transform: scale(1);
    opacity: 1;
}

.metric-card-check i { color: inherit !important; }

.metric-card-illustration {
    width: 56px;
    height: 56px;
    border-radius: var(--v2-radius-md);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    flex-shrink: 0;
}

.metric-card-illustration i { color: inherit !important; }

.metric-card-illustration--card {
    background: linear-gradient(135deg, rgba(8, 145, 178, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%);
    color: #0e7490;
}

.metric-card-illustration--gauge {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
    color: #4338ca;
}

.metric-card-illustration--pie {
    background: linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(219, 39, 119, 0.15) 100%);
    color: #be185d;
}

.metric-card-illustration--bar {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(20, 184, 166, 0.15) 100%);
    color: #047857;
}

.metric-card-illustration--line {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(249, 115, 22, 0.15) 100%);
    color: #b45309;
}

.metric-card-illustration--area {
    background: linear-gradient(135deg, rgba(20, 184, 166, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%);
    color: #0f766e;
}

.metric-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.metric-card-title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    line-height: 1.3;
    letter-spacing: -0.005em;
}

.metric-card-desc {
    margin: 0;
    font-size: 0.8rem;
    color: var(--v2-text-3);
    line-height: 1.5;
}

.metric-card-meta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    padding-top: 8px;
    border-top: 1px solid var(--v2-border-subtle);
    margin-top: auto;
}

.metric-card-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 999px;
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.metric-card-tag i {
    font-size: 0.62rem;
    color: inherit !important;
}

.metric-card-tag--display {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
}

.metric-card--selected .metric-card-tag--display {
    background: var(--v2-primary);
    color: #fff;
}

/* Empty */
.sc-empty {
    text-align: center;
    padding: 64px 24px;
    background: var(--v2-surface-0);
    border: 1px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.sc-empty i {
    font-size: 2.6rem;
    color: var(--v2-text-4);
    display: block;
    margin-bottom: 14px;
}

.sc-empty h3 {
    margin: 0 0 6px;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
}

.sc-empty p {
    margin: 0 auto;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    max-width: 360px;
    line-height: 1.5;
}

@media (max-width: 640px) {
    .sc-v2 { padding: 18px 16px 32px; }
    .sc-hero-actions { flex-basis: 100%; }
    .sc-hero-actions :deep(.bmv-btn) { flex: 1; }
    .sc-stats { padding: 12px; gap: 12px; }
    .sc-stats-actions { flex-basis: 100%; justify-content: flex-end; }
    .sc-grid { grid-template-columns: 1fr; }
    .metric-card { padding: 16px; }
}
</style>
